import React, { useState, useEffect } from 'react';
import { Page, Dashboard } from '../../templates';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { FlexBoxVertical, FlexBoxHorizontal, BackdropPanel } from '../../atoms';
import { LanguageButton, InfoTooltip, SearchButton } from '../../molecules';
import i18n from '../../../utils/i18n';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import TestReportFilter from './Filter';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinearProgress from '@mui/material/LinearProgress';
import palette from '../../../utils/colorPalette';
import ChatPanel from './ChatPanel';
import { translateText } from '../../../utils/translateText';
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { LiaDownloadSolid } from "react-icons/lia";
import { MdOutlineRestartAlt } from "react-icons/md";
import Menu from '../../../utils/menu';

function TestReport() {
  const apiRef = useGridApiRef();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [cnt, setCnt] = useState(0);
  const [rawCnt, setRawCnt] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [lastUpdate, setLastUpdate] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const language = i18n.language;
  const { t } = useTranslation();
  const [translatedColumns, setTranslatedColumns] = useState(columns(language, apiRef));
  

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      setMessages([...messages, { text: inputMessage, sender: 'user' }]);
      setInputMessage('');
      // AI 응답 로직 추가 예정
    }
  };

  useEffect(() => {
    setTranslatedColumns(columns(language, apiRef).map((column) => {
      return {
        ...column, 
        headerName: translateText({
          text: column.headerName,
          globalLanguage: language,
        }),
      };
    }));
  }, [language]);

  const pdfDownload = async () => {
    const selectedSet = new Set(rowSelectionModel);
    const downloadFiles = tableData.filter((item) => selectedSet.has(item.no));
    
    if (downloadFiles.length === 0) {
      alert(translateText({text: '다운로드할 파일을 선택해주세요.', globalLanguage: language}));
      return;
    }

    const controller = new AbortController(); // 새로운 AbortController 생성
    setAbortController(controller); // 상태에 저장

    try {
      setIsDownloading(true); // 다운로드 시작
      const response = await fetch('/api/test-report/download-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pdfs: downloadFiles.map(item => ({
            url: item.dashff_url,
            fileName: item.orgn_file_nm
          }))
        }),
        signal: controller.signal // 요청에 signal 추가
      });

      if (!response.ok) {
        throw new Error('파일 다운로드 실패');
      }

      const blob = await response.blob();
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = 'test_reports.zip';  // 파일명만 지정하고 경로는 지정하지 않음
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('다운로드가 취소되었습니다.'); // 다운로드 취소 로그
      } else {
        console.error('파일 다운로드 중 오류 발생:', error);
        alert('파일 다운로드 중 오류가 발생했습니다.');
      }
    } finally {
      setIsDownloading(false); // 다운로드 종료
      setAbortController(null); // AbortController 상태 초기화
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetch('/api/test-report/filter-options', {
      method: 'GET', 
      headers: {
        'Content-Type': 'application/json'
      },
    })
      .then(response => response.json())
      .then(data => {
        // setTableData(data.data)
        setLastUpdate(data.lastUpdate)
        setFilterOptions({
          sesn: Array.isArray(data.season) 
            ? data.season.map(item => ({
                value: item.SEASON,
                label: item.SEASON,
                label_en: item.SEASON
              }))
            : [],
          po_cntry: [
            { value: 'CN', label: 'CN', label_en: 'CN' },
            { value: 'KR', label: 'KR', label_en: 'KR' },
            { value: 'TW', label: 'TW', label_en: 'TW' },
          ],
          certi_type:  [
            { value: 'GB', label: 'GB', label_en: 'GB' },
            { value: 'GB_F', label: 'GB(기능성)', label_en: 'GB_F' },
            { value: 'KC', label: 'KC', label_en: 'KC' },
            { value: 'KC_KIDS', label: '아동KC', label_en: 'KC_KIDS' },
          ],
          gubun: [
            { value: 'PRODUCT', label: '완제품', label_en: 'PRODUCT' },
            { value: 'C1', label: 'C1', label_en: 'C1' },
            { value: 'C2', label: 'C2', label_en: 'C2' },
            { value: 'C3', label: 'C3', label_en: 'C3' },
            { value: 'C4', label: 'C4', label_en: 'C4' },
            { value: 'C5', label: 'C5', label_en: 'C5' },
            { value: 'ETC', label: 'ETC', label_en: 'ETC' },
            { value: 'SHELL', label: 'SHELL', label_en: 'SHELL' },
          ],
          fab_type: [
            { value: '-', label: '-', label_en: '-' },
            { value: 'ACC', label: 'ACC', label_en: 'ACC' },
            { value: 'KNIT', label: 'KNIT', label_en: 'KNIT' },
            { value: 'SWEATER', label: 'SWEATER', label_en: 'SWEATER' },
            { value: 'WOVEN', label: 'WOVEN', label_en: 'WOVEN' },
          ],
          mfac_compy_nm: Array.isArray(data.vendor) 
            ? data.vendor.map(item => ({
                value: item.mfac_compy_nm,
                label: item.mfac_compy_nm,
                label_en: item.mfac_compy_nm_en
              }))
            : [],
          dsgnr_goods_nm: Array.isArray(data.goodsDesigner) 
            ? data.goodsDesigner.map(item => ({
                value: item.dsgnr_goods_nm,
                label: item.dsgnr_goods_nm,
                label_en: item.dsgnr_goods_nm_en
              }))
            : [],
          dsgnr_mtrls_nm: Array.isArray(data.matrialDesigner) 
            ? data.matrialDesigner.map(item => ({
                value: item.dsgnr_mtrls_nm,
                label: item.dsgnr_mtrls_nm,
                label_en: item.dsgnr_mtrls_nm_en
              }))
            : [],
        });
      })
      .catch(error => {
        console.error('Error fetching Snowflake data:', error);
      });
    setTimeout(() => {setIsLoading(false)}, 2000);
  }, []);

  const filterData = (options) => {
    console.log(options);
    setIsLoading(true);

    const hasValue = (value) => value && value.trim() !== '';
    const joinArrayValues = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return null;
      const joined = arr.map(item => `'${item.value}'`).join(',');
      return joined || null;
    };
    
    const selectedOptions = {
      season: options.sesn.value,
      po_cntry: joinArrayValues(options.po_cntry),
      certi_type: joinArrayValues(options.certi_type),
      gubun: joinArrayValues(options.gubun),
      fab_type: joinArrayValues(options.fab_type),
      dsgnr_goods_nm: joinArrayValues(options.dsgnr_goods_nm),
      dsgnr_mtrls_nm: joinArrayValues(options.dsgnr_mtrls_nm),
      mfac_compy_nm: joinArrayValues(options.mfac_compy_nm),
      searchValue: hasValue(options.searchValue) ? options.searchValue : null,
      fabInfo: hasValue(options.fabInfo) ? options.fabInfo : null,
    }

    selectedOptions.gubun = selectedOptions.gubun ? selectedOptions.gubun.replace('PRODUCT', '*') : null;
    selectedOptions.gubun = selectedOptions.gubun ? selectedOptions.gubun.replace('SHELL', 'G') : null;

    console.log(selectedOptions);

    // API 호출
    fetch('/api/test-report', {
      method: 'POST',
      body: JSON.stringify(selectedOptions),
      headers: {
        'Content-Type': 'application/json'
      },
    })
      .then(response => response.json())
      .then(data => {
        console.log(data);
        if (data) {
          setTableData(data.data);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching test report data:', error);
        setIsLoading(false);
      });
  };

  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      <Dashboard style={{
        backgroundColor: 'white',
      }}>
        
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: 1,
            width: '98%',
            margin: '10px',
            marginBottom: '30px',
          }}
        >
          <FlexBoxHorizontal>
              <FlexBoxVertical style={{ 
                overflow: 'hidden',
              }}>
                {filterOptions ? <TestReportFilter data={filterOptions} onSearch={filterData} /> : <></>}
                <FlexBoxHorizontal style={{
                  justifyContent: 'space-between',
                  width: '100%',
                  height: '40px',
                  marginTop: '10px',
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}>
                      <div style={{ position: 'relative', width: '130px' }}>
                        <IconButton
                          onClick={() => {
                            if (isDownloading) {
                              abortController.abort();
                              setIsDownloading(false);
                            } else {
                              pdfDownload();
                            }
                          }}
                          sx={{
                            padding: '4px',
                            width: '100%',
                            height: '27px',
                            borderRadius: '0',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            transition: 'background-color 0.1s ease',
                            fontSize: '12px',
                          }}
                        >
                          <SystemUpdateAltIcon sx={{ fontSize: 16, color: '#666', marginRight: '5px' }} />
                          {isDownloading ? (
                            <>
                              <span style={{ margin: '0 5px 0 0' }}>{translateText({text: '다운로드 중...', globalLanguage: language})}</span> 
                              <span style={{ color: 'rgb(255, 109, 135)', fontWeight: 'bold' }}>X</span>
                            </>
                          ) : (
                            translateText({text: 'PDF 다운로드', globalLanguage: language})
                          )}
                        </IconButton>
                        {isDownloading && (
                          <LinearProgress
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              width: '100%',
                              height: '2px',
                              backgroundColor: 'rgba(97, 97, 230, 0.2)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: palette.button.primary
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#353339',
                    }}>
                      selected: {rowSelectionModel.length}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#353339',
                    marginRight: '10px',
                  }}>
                    last update: {lastUpdate}
                  </div>
                </FlexBoxHorizontal>
                <DataGridPremium
                  apiRef={apiRef}
                  sx={style}
                  rows={tableData}
                  columns={translatedColumns}
                  getRowId={(row) => row['no']}
                  hideFooter
                  rowHeight={35}
                  onRowClick={(params) => {
                    setSelectedRow(params.row);
                  }}
                  checkboxSelection
                  disableRowSelectionOnClick
                  onRowSelectionModelChange={(newRowSelectionModel) => {
                    setRowSelectionModel(newRowSelectionModel);
                  }}
                  getRowClassName={(params) => {
                    return params.row.no === selectedRow?.no ? 'selected-row' : '';
                  }}
                  initialState={{
                    pinnedColumns: {
                      right: ['refreshButton', 'excelButton'],
                    },
                  }}
                />
              </FlexBoxVertical>
              <FlexBoxVertical style={{ width: '60%' }}>
                <div style={{
                  height: '30px',
                  marginRight: '10px',
                }}>
                  <LanguageButton />
                </div>
              <FlexBoxHorizontal style={{
                  justifyContent: 'flex-end',
                  width: '100%',
                  height: '40px',
                }}>
                <InfoTooltip
                  content={`[ ctrl + 마우스 스크롤 ] 하시면 \n 화면 축소/확대 가능합니다`}
                  style={{ marginRight: '4px', color: '#353339' }}
                />
                <a
                  href={`${selectedRow.dashff_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                  onClick={(e) => {
                    if (!selectedRow.dashff_url) {
                      e.preventDefault();
                      alert(translateText({text: '파일을 선택하세요.', globalLanguage: language}));
                    }
                  }}
                >
                  <SearchButton
                    label={'PDF 열기'}
                    style={{ minWidth: '70px', cursor: 'pointer' }}
                  />
                </a>
                <SearchButton
                  style={{ minWidth: '70px', cursor: 'pointer' }}
                  label={'AI Chat'}
                  onClick={() => {
                    if (!selectedRow.dashff_url) {
                      alert(translateText({text: '파일을 선택하세요.', globalLanguage: language}));
                      return;
                    }
                    setChatOpen(!chatOpen);
                  }}
                />
                <SearchButton
                    label={'F&Q'}
                    style={{ minWidth: '70px', cursor: 'pointer' }}
                    onClick={() => window.open('https://fnf-digital.notion.site/FAQ_TEST-Dashboard-203fd8bcf5588014af6ad1a1ee9f86a2', '_blank')}
                  />
                  <SearchButton
                    label={'매뉴얼'}
                    style={{ minWidth: '70px', cursor: 'pointer' }}
                    onClick={() => setSelectedRow({dashff_url: 'https://static-dashff.fnf.co.kr/interstellar/test_report/%E2%98%85DISCOVERY+%ED%92%88%EC%A7%88%EB%A9%94%EB%89%B4%EC%96%BC(GB%2BKC)+(251117)(V.2).pdf'})}
                  />
                </FlexBoxHorizontal>
                <>
                <div style={{
                  width: '99%',
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  marginTop: '10px',
                }}>
            {!!selectedRow && !!selectedRow.dashff_url ? (
              
            <embed
              style={{
                width: '100%',
                height: '100%',
                zoom: '1',
              }}
              src={`${selectedRow.dashff_url}#toolbar=0`}
              type="application/pdf"
              onLoadStart={() => setIsLoading(true)}
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                alert('PDF를 로드하는 데 문제가 발생했습니다. 다시 시도해주세요.');
              }}
            />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                {translateText({text: '파일을 선택하세요.', globalLanguage: language})}
              </div>
            )}
            </div>
            
            {chatOpen && (
              <div
                style={{
                  position: 'fixed',
                  right: 200,
                  top: 100,
                  width: '300px',
                  height: '80vh',
                  zIndex: 1000,
                  pointerEvents: 'auto'
                }}
              >
                <ChatPanel onClose={() => setChatOpen(false)} selectedFile={selectedRow} />
              </div>
            )}
            </>
            </FlexBoxVertical>
          </FlexBoxHorizontal>
        </div>
      </Dashboard>
    </Page>
  );
}

export default TestReport;

const columns = (language, apiRef) => [
  {
    field: 'grp_po_no',
    headerName: '그룹번호',
    width: 130,
  },
  {
    field: 'style_po_no',
    headerName: '발주번호',
    width: 130,
  },
  {
    field: 'color',
    headerName: '컬러',
    width: 60,
  },
  {
    field: 'indc_dt_cnfm',
    headerName: '합의납기일',
    width: 100,
  },
  {
    field: language === 'ko' ? 'type' : 'type_en',
    headerName: '원단/제품',
    width: 70,
  },
  {
    field: language === 'ko' ? 'certi_type' : 'certi_type_en',
    headerName: '인증유형',
    width: 80,
  },
  {
    field: language === 'ko' ? 'gubun' : 'gubun_en',
    headerName: '분류',
    width: 80,
  },
  {
    field: 'fab_info',
    headerName: '패브릭 ',
    width: 120,
  },
  {
    field: 'seq',
    headerName: '순번',
    width: 60,
  },
  
  {
    field: 'pass_yn',
    headerName: '통과여부',
    width: 60,
  },
  {
    field: 'orgn_file_nm',
    headerName: '파일명',
    width: 700,
  },
  {
    field: 'excelButton',
    type: 'actions',
    headerName: '엑셀버튼',
    width: 150,
    renderHeader: () => (
      <>
      <button style={{
        cursor: 'pointer',
        fontSize: '12px',
        color: 'rgba(0, 0, 0, 0.54)',
        borderRadius: '5px',
        border: 'none',
        // backgroundColor: '#f5f5f5',
        backgroundColor: 'white',
        padding: '2px 5px',
        transition: 'background-color 0.2s ease',
        height: '30px',
        marginRight: '5px',
      }} 
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
      }}
      onClick={() => {
        const columns = apiRef.current.getAllColumns();
        columns.forEach(column => {
          apiRef.current.sortColumn(column.field, null);
        });
      }}
      >
        <MdOutlineRestartAlt style={{ marginBottom: '-3px', fontSize: '20px' }}/>
        <span style={{verticalAlign: '3px', marginTop: '-5px' }}>refresh</span>
      </button>
      <button style={{
        cursor: 'pointer',
        fontSize: '20px',
        color: 'rgba(0, 0, 0, 0.54)',
        borderRadius: '5px',
        border: 'none',
        // backgroundColor: '#f5f5f5',
        backgroundColor: 'white',
        padding: '2px 5px',
        transition: 'background-color 0.2s ease',
        height: '30px',
      }} 
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
      }}
      onClick={() => {
        apiRef.current.exportDataAsExcel({
          getRowsToExport: (params) => params.apiRef.current.getAllRowIds(),
          fileName: 'test_report',
          exceljsPostProcess: (params) => {
            params.worksheet.eachRow((row) => {
              row.eachCell((cell) => {
                cell.value = cell.value === `'-` ? null : cell.value;
              });
            });
          }
        });
      }}>
        <LiaDownloadSolid style={{ marginBottom: '-3px' }}/>
        <PiMicrosoftExcelLogoFill style={{ marginBottom: '-3px' }}/>
      </button>
      </>
    ),
    getActions: () => [],
    cellClassName: 'excel-button-cell',
  },
];

const style = {
  width: '100%',
  fontSize: '0.7rem',
  '& .MuiDataGrid-columnHeader': {
    // backgroundColor: 'rgba(229, 221, 243, 0.12) !important',
    // height: '40px !important',
    backgroundColor: 'white !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
    fontSize: '0.7rem',
  },
  '& .Mui-selected': {
    backgroundColor: 'white !important',
  },
  '& .MuiCheckbox-root': {
    color: '#666666',
    '&.Mui-checked': {
      color: '#666666',
    }
  },
  '& .MuiDataGrid-row.selected-row': {
      backgroundColor: 'rgba(224, 236, 251, 0.76) !important',
    },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    backgroundColor: 'white !important',
    '&:hover': {
      backgroundColor: 'rgba(195, 196, 197, 0.2) !important',
    }
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none'
  },
  '& .MuiDataGrid-cell--pinnedRight': {
    borderLeft: 'none',
    position: 'unset',
    backgroundColor: 'white !important',
  },
  '& .MuiDataGrid-filler--pinnedRight': {
    borderLeft: 'none',
    position: 'unset',
  },
  '& .MuiDataGrid-columnHeader--withLeftBorder': {
    borderLeft: 'none',
  },
};

const createOptions = (data, key, filterFn = (value) => value !== null) => {
  const options = Array.from(
    new Set(
      data.map((item) => (item[key] === null ? '-' : item[key])).filter(filterFn)
    )
  ).map((value) => {
    const item = data.find(item => item[key] === value);
    return {
      value: value,
      label: value,
      label_en: item?.[key + '_en'] ? `${item[key + '_en']}` : value
    };
  });

  

  if ( key === 'certi_type') {
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.label_en, item])).values()
    );
    return distinctOptions.sort((a, b) => {
      if (a.label_en === '-') return -1;
      if (b.label_en === '-') return 1;
      return a.label_en.localeCompare(b.label_en);
    });
  } else {
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.value, item])).values()
    );
    return distinctOptions.sort((a, b) => {
      if (a.value === '-') return -1;
      if (b.value === '-') return 1;
      return a.value.localeCompare(b.value);
  });
  }
};